import React, {useCallback, useEffect, useState} from 'react'
import {Checkbox, DatePicker, Form, Input, Popover, Slider, Tag, TimePicker} from 'antd'
import FilterButton from 'src/components/elements/Buttons/FilterButton/FilterButton'
import {FiltersStyled} from './FiltersStyled'
import {FormattedMessage} from 'react-intl'
import Select from 'antd/lib/select'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import {Button, Typography} from '@mui/material'
import locale from 'antd/es/date-picker/locale/it_IT'
import Icon from '@mdi/react'
import {mdiClose, mdiMagnify} from '@mdi/js'
import PatientLookupDialog from '../PatientLookupDialog/PatientLookupDialog'
import {ClickAwayListener} from '@mui/base'
import DoctorsLookupDialog from '../DoctorsLookupDialog/DoctorsLookupDialog'
import type {Moment} from 'moment'

export type FilterItem = {
  id: string;
  label: string;
  type: string;
  // [Moment, Moment] pre-fills a 'rangePicker' (antd v4 date pickers work on moment).
  defaultValue?: string[] | [number, number] | [Moment, Moment];
  options?: {
    label: string,
    value: string
  }[];
  // Alternative to static `options`, for a field whose choices come from an API call
  // instead of a fixed list (e.g. province/città cascading off region/provincia). Called
  // on mount for a field with no `dependsOn`, and again whenever the field it `dependsOn`
  // changes — receives the current filter values so it can scope the request accordingly.
  loadOptions?: (values: Record<string, any>) => Promise<{label: string, value: string}[]>;
  // Id of another FilterItem in the same config this field's options depend on. While
  // that field is empty, this one renders disabled instead of calling `loadOptions`; once
  // it changes, this field's own value is cleared (and its `loadOptions` re-run) so it
  // never shows stale choices from the previous parent selection.
  dependsOn?: string;
  min?: number;
  max?: number;
  step?: number;
  getLabel?: (value: [number, number]) => string;
  valueLabelFormat?: (value?: number) => string;
};

type FiltersProps = {
  formId?: string,
  config: FilterItem[];
  onSubmit: (values: any) => void;
  doctorCustomFilters?: Record<string, string | string[]>[];
};

const Filters: React.FC<FiltersProps> = ({formId = 'filter-form', config = [], onSubmit, doctorCustomFilters = []}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [isPatientsModal, setIsPatientsModal] = useState<boolean>(false)
  const [patient, setPatient] = useState<Record<string, any>>()
  const [isDoctorsModal, setIsDoctorsModal] = useState<boolean>(false)
  const [doctor, setDoctor] = useState<Record<string, any>>()
  const [numOptionSelected, setNumOptionSelected] = useState(0)
  const [form] = Form.useForm()
  const {RangePicker} = DatePicker
  // Resolved options for `loadOptions` fields (region/provincia/città-style cascades) and
  // their in-flight state — kept apart from `options`, which stays whatever the config
  // declared, so a field can fall back to it if `loadOptions` was never configured.
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, {label: string, value: string}[]>>({})
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})
  // Watching the whole form (rather than one field at a time) keeps this to a single hook
  // call regardless of how many fields declare a dependsOn — calling Form.useWatch per
  // config item inside .map would vary the hook count across renders.
  const watchedValues = Form.useWatch([], form) ?? {}

  const getOptions = (element: FilterItem): {label: string, value: string}[] =>
    element.loadOptions ? (dynamicOptions[element.id] ?? []) : (element.options ?? [])

  const loadOptionsFor = async (element: FilterItem, valuesForLoad: Record<string, any>) => {
    if (!element.loadOptions) return
    setLoadingFields(prev => ({...prev, [element.id]: true}))
    try {
      const opts = await element.loadOptions(valuesForLoad)
      setDynamicOptions(prev => ({...prev, [element.id]: opts ?? []}))
    } catch {
      setDynamicOptions(prev => ({...prev, [element.id]: []}))
    } finally {
      setLoadingFields(prev => ({...prev, [element.id]: false}))
    }
  }

  // All fields (transitively) depending on `id`, closest first — used to clear a whole
  // cascade (e.g. changing regione clears both provincia and città, not just provincia).
  const collectDependentIds = (id: string): string[] => {
    const direct = config.filter((item) => item.dependsOn === id).map((item) => item.id)
    return direct.reduce<string[]>((acc, depId) => [...acc, ...collectDependentIds(depId)], [...direct])
  }

  const handleValuesChange = (changedValues: Record<string, any>) => {
    const changedId = Object.keys(changedValues)[0]
    if (!changedId) return
    const dependentIds = collectDependentIds(changedId)
    if (dependentIds.length) {
      const resetValues: Record<string, any> = {}
      dependentIds.forEach((depId) => { resetValues[depId] = undefined })
      form.setFieldsValue(resetValues)
      setDynamicOptions(prev => {
        const next = {...prev}
        dependentIds.forEach((depId) => { next[depId] = [] })
        return next
      })
    }
    const newValue = changedValues[changedId]
    if (newValue) {
      config
        .filter((item) => item.dependsOn === changedId && item.loadOptions)
        .forEach((item) => loadOptionsFor(item, {...form.getFieldsValue(), [changedId]: newValue}))
    }
  }

  useEffect(() => {
    config.forEach(({id, defaultValue}) => form.setFieldValue(id, defaultValue))
    // Root fields (no dependsOn) with loadOptions fetch immediately; dependent fields wait
    // for their parent to be picked (see handleValuesChange above).
    config
      .filter((item) => item.loadOptions && !item.dependsOn)
      .forEach((item) => loadOptionsFor(item, form.getFieldsValue()))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clipLabelTag = (label: any, numSedi:any) => {
    switch (numSedi) {
      case 1:
        return label.slice(0, 14)
      case 2:
        return label.slice(0, 6)
      case 3:
        return label.slice(0, 3)
      default:
        return label.slice(0, 3)
    }
  }

  const tagRender = useCallback((property) => {
    const {label, closable, onClose} = property
    const onPreventMouseDown = (event: any) => {
      event.preventDefault()
      event.stopPropagation()
    }
    const labelToRender = clipLabelTag(label, numOptionSelected)

    return (
      <Tag
        closable={closable}
        onClose={onClose}
        onMouseDown={onPreventMouseDown}
        role='option'
      >
        {label === labelToRender ? labelToRender : labelToRender + '..'}
      </Tag>
    )
  }, [numOptionSelected])

  const onFinish = (values: any) => {
    const postBody = Object.keys(values).map((key) => {
      const element = config.find((el: FilterItem) => el.id === key)
      if (element?.type === 'rangePicker' && values[key]) {
        return {
          property: key,
          type: element.type,
          value: [values[key]?.at(0).startOf('day').toISOString(), values[key]?.at(1)?.endOf('day').toISOString()]
        }
      }
      if (element?.type === 'datePicker' && values[key]) {
        return {
          property: key,
          type: element.type,
          value: values[key]?.startOf('day').toISOString()
        }
      }
      if (element?.type === 'patientLookup') {
        return {
          property: key,
          type: element.type,
          value: patient
        }
      }
      if (element?.type === 'doctorLookup') {
        return {
          property: key,
          type: element.type,
          value: doctor
        }
      }
      if (element?.type === 'range' && values[key]) {
        return {
          property: key,
          type: element.type,
          value: values[key]
        }
      }
      // Fall back to 'text' when the PSP config omits `type` on a filter item: the
      // backend's schema requires it, and `undefined` gets silently dropped by JSON
      // serialization, causing "body.filters.N must have required property 'type'".
      return {
        property: key,
        type: element?.type ?? 'text',
        value: values[key]
      }
    }).filter((el) => el.value)
    onSubmit(postBody)
  }

  const onCancel = () => {
    config.forEach(({id, defaultValue}) => form.setFieldValue(id, defaultValue))
    // Root loadOptions fields (e.g. regione) keep their already-fetched list — it doesn't
    // depend on any filter value — only dependent fields (provincia/città) need clearing.
    setDynamicOptions(prev => {
      const next = {...prev}
      config.forEach((item) => { if (item.dependsOn) next[item.id] = [] })
      return next
    })
    form.submit()
  }

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (open) {
      setOpen(false)
    } else {
      setOpen(true)
    }
  }

  const getFormItem = (element: FilterItem) => {
    switch (element.type) {
      case 'datePicker':
        return (
          <DatePicker
            format='DD MMM YYYY'
            locale={locale}
            placeholder=''
            popupStyle={{zIndex: 1400}}
          />
        )
      case 'timePicker':
        return (
          <TimePicker
            format='HH:mm'
            locale={locale}
            placeholder=''
            popupStyle={{zIndex: 1400}}
            showNow={false}
          />
        )
      case 'rangePicker':
        return (
          <RangePicker
            format='DD MMM YYYY'
            locale={locale}
            placeholder={['', '']}
            popupStyle={{zIndex: 1400}}
          />
        )
      case 'range':
        return (
          <Slider
            max={element.max}
            min={element.min}
            range
            step={element.step}
            tooltip={{
              formatter: element.valueLabelFormat
            }}
          />
        )
      case 'checkbox':
        return (
          <Checkbox.Group>
            {
              getOptions(element).map((option) => (
                <Checkbox key={option.value} value={option.value}>
                  <Typography variant='body2'>{option.label}</Typography>
                </Checkbox>
              ))
            }
          </Checkbox.Group>
        )
      case 'select': {
        const parentItem = element.dependsOn ? config.find((item) => item.id === element.dependsOn) : undefined
        const waitingForParent = Boolean(parentItem) && !watchedValues[parentItem!.id]
        const isLoading = Boolean(loadingFields[element.id])
        return (
          <Select
            allowClear
            disabled={waitingForParent}
            dropdownStyle={{zIndex: 1400}}
            getPopupContainer={(targetNode) => targetNode}
            loading={isLoading}
            options={waitingForParent ? [] : getOptions(element)}
            placeholder={waitingForParent ? `Seleziona prima ${parentItem!.label.toLowerCase()}` : 'Cerca...'}
            showSearch
          />
        )
      }
      case 'multiSelect':
        return (
          <Select
            allowClear
            dropdownStyle={{zIndex: 1400}}
            getPopupContainer={(targetNode) => targetNode}
            maxTagCount={2}
            maxTagPlaceholder={(omit) => `+${omit.length}..`}
            mode='multiple'
            onChange={(selected) => setNumOptionSelected(selected.length)}
            options={getOptions(element)}
            tagRender={tagRender}
          />
        )
      case 'patientLookup':
        return (
          <div className='user-content'>
            <Form.Item
              name={'patient'}
            >
              <Input disabled />
            </Form.Item>
            <div className='user-buttons'>
              <Button
                className='user-button'
                onClick={() => setIsPatientsModal(true)}
              >
                <Icon path={mdiMagnify} size={1} />
              </Button>
              <Button
                className='user-button'
                onClick={() => { form.setFieldValue(element.id, undefined); setPatient(undefined) }}
              >
                <Icon path={mdiClose} size={1} />
              </Button>
            </div>
          </div>
        )
      case 'doctorLookup':
        return (
          <div className='user-content'>
            <Form.Item
              name={'doctor'}
            >
              <Input disabled />
            </Form.Item>
            <div className='user-buttons'>
              <Button
                className='user-button'
                onClick={() => setIsDoctorsModal(true)}
              >
                <Icon path={mdiMagnify} size={1} />
              </Button>
              <Button
                className='user-button'
                onClick={() => { form.setFieldValue(element.id, undefined); setDoctor(undefined) }}
              >
                <Icon path={mdiClose} size={1} />
              </Button>
            </div>
          </div>
        )
      default:
        return <Input />
    }
  }

  const menu = (
    <FiltersStyled>
      <Form
        className='filter-form'
        form={form}
        id={formId}
        layout='vertical'
        onFinish={(values) => { onFinish(values) }}
        onValuesChange={handleValuesChange}
        style={{gridTemplateColumns: `repeat(${config.length >= 3 ? 3 : config.length}, 1fr)`}}
      >
        {config.map((element) => {
          return (
            <Form.Item
              key={element.id}
              label={element.label}
              name={element.id}
            >
              {getFormItem(element)}
            </Form.Item>
          )
        })}
      </Form>
      <div className='footer-div'>
        <PrimaryButton
          form={formId}
          key='submit'
          type='submit'
        >
          <FormattedMessage id='search' />
        </PrimaryButton>
        <SecondaryButton onClick={() => onCancel()}>
          <FormattedMessage id='cancel' />
        </SecondaryButton>
      </div>
    </FiltersStyled>
  )

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className='filters'>
        <Popover
          content={menu}
          getPopupContainer={(triggerNode) => triggerNode}
          open={open}
          placement='bottomLeft'
          showArrow={false}
          trigger={'click'}
        >
          <FilterButton
            onClick={handleClick}
            open={open}
          />
        </Popover>
        {isPatientsModal &&
          <PatientLookupDialog
            onClose={() => setIsPatientsModal(false)}
            onRowClick={(row) => {
              setPatient(row)
              form.setFieldValue('patient', `${row.firstName} ${row.lastName}`)
              setIsPatientsModal(false)
            }}
          />}
        {isDoctorsModal &&
          <DoctorsLookupDialog
            customFilters={doctorCustomFilters}
            onClose={() => setIsDoctorsModal(false)}
            onRowClick={(row) => {
              setDoctor(row._id)
              form.setFieldValue('doctor', `${row.firstName} ${row.lastName}`)
              setIsDoctorsModal(false)
            }}
          />}
      </div>
    </ClickAwayListener>
  )
}

export default Filters
